import React, { useMemo, useRef, useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, ListGroup } from "react-bootstrap";
import { GoogleMap, Marker, Autocomplete } from "@react-google-maps/api";
import "./index.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import { useGoogleMaps } from "../../hooks/useGoogleMaps";

export interface AddressItem {
  id?: string | number;
  tag?: string;
  address: string;
  latitude?: number;
  longitude?: number;
}

interface DeliveryAddressModalProps {
  show: boolean;
  onHide: () => void;
  addresses: AddressItem[];
  onSelect: (address: AddressItem) => void;
  isLoadingAddresses?: boolean;
}

const DeliveryAddressModal: React.FC<DeliveryAddressModalProps> = ({
  show,
  onHide,
  addresses,
  onSelect,
  isLoadingAddresses = false,
}) => {
  // const [newTag, setNewTag] = useState<string>("");
  // const [newAddress, setNewAddress] = useState<string>("");
  const [filter, setFilter] = useState<string>("");

  // Filter addresses based on search term
  const filteredAddresses = useMemo(() => {
    if (!addresses) return [];
    if (!filter.trim()) return addresses;
    
    const searchTerm = filter.toLowerCase();
    return addresses.filter(addr => 
      addr.tag?.toLowerCase().includes(searchTerm) ||
      addr.address.toLowerCase().includes(searchTerm)
    );
  }, [addresses, filter]);

  // Map state
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>({ lat: 6.9270786, lng: 79.861243 });
  const [selectedLatLng, setSelectedLatLng] = useState<google.maps.LatLngLiteral | null>(null);
  const [selectedFormattedAddress, setSelectedFormattedAddress] = useState<string>("");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Current location state
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentLocation, setCurrentLocation] = useState<google.maps.LatLngLiteral | null>(null);
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hasLocationPermission, setHasLocationPermission] = useState<boolean | null>(null);
  const [isResolvingUrl, setIsResolvingUrl] = useState(false);

   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   const { isLoaded, loadError } = useGoogleMaps();
 

  // const getTagIcon = (tag?: string) => {
  //   const option = tagOptions.find(opt => opt.value === tag);
  //   return option?.icon || faMapMarkerAlt;
  // };

  // Check geolocation permissions on component mount
  useEffect(() => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setHasLocationPermission(result.state === 'granted');

        result.addEventListener('change', () => {
          setHasLocationPermission(result.state === 'granted');
        });
      });
    }
  }, []);

  // Get user's current location
  // const getCurrentLocation = () => {
  //   if (!navigator.geolocation) {
  //     setLocationError("Geolocation is not supported by this browser.");
  //     return;
  //   }

  //   setIsGettingLocation(true);
  //   setLocationError(null);

  //   const options = {
  //     enableHighAccuracy: true,
  //     timeout: 10000,
  //     maximumAge: 60000
  //   };

  //   navigator.geolocation.getCurrentPosition(
  //     (position) => {
  //       const location = {
  //         lat: position.coords.latitude,
  //         lng: position.coords.longitude
  //       };

  //       setCurrentLocation(location);
  //       setSelectedLatLng(location);
  //       setMapCenter(location);
  //       setIsGettingLocation(false);

  //       // Reverse geocode to get address
  //       geocodeLatLng(location);

  //     },
  //     (error) => {
  //       setIsGettingLocation(false);
  //       let errorMessage = "";

  //       switch (error.code) {
  //         case error.PERMISSION_DENIED:
  //           errorMessage = "Location access denied by user.";
  //           setHasLocationPermission(false);
  //           break;
  //         case error.POSITION_UNAVAILABLE:
  //           errorMessage = "Location information is unavailable.";
  //           break;
  //         case error.TIMEOUT:
  //           errorMessage = "Location request timed out.";
  //           break;
  //         default:
  //           errorMessage = "An unknown error occurred while getting location.";
  //           break;
  //       }

  //       setLocationError(errorMessage);
  //       console.error("Geolocation error:", error);
  //     },
  //     options
  //   );
  // };

  const geocodeLatLng = (latLng: google.maps.LatLngLiteral) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        setSelectedFormattedAddress(results[0].formatted_address);
      } else {
        setSelectedFormattedAddress(`${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}`);
      }
    });
  };

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    const latLng = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setSelectedLatLng(latLng);
    setMapCenter(latLng);
    geocodeLatLng(latLng);
  };

  const onAutocompleteLoad = (ac: google.maps.places.Autocomplete) => {
    autocompleteRef.current = ac;
  };

  // Function to resolve Google Maps short URLs using multiple methods
  const resolveShortUrl = async (shortUrl: string): Promise<string | null> => {
    try {
      // Method 1: Try extracting from URL patterns first (works for some share URLs)
      const directExtract = tryExtractFromShareUrl(shortUrl);
      if (directExtract) return directExtract;

      // Method 2: Try working CORS proxies with timeout
      const corsProxies = [
        { url: `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(shortUrl)}`, type: 'text' },
        { url: `https://cors.bridged.cc/${shortUrl}`, type: 'text' },
        { url: `https://yacdn.org/proxy/${shortUrl}`, type: 'text' }
      ];

      const timeoutPromise = (ms: number) => new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), ms)
      );

      for (const proxy of corsProxies) {
        try {          
          const response = await Promise.race([
            fetch(proxy.url, {
              method: 'GET',
              headers: {
                'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
                'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
              }
            }),
            timeoutPromise(5000) // 5 second timeout
          ]);
          
          if (response instanceof Response && response.ok) {
            const content = await response.text();
            const coords = extractCoordsFromContent(content);
            if (coords) {
              return coords;
            }
          }
        } catch (e) {
          console.warn(`Proxy ${proxy.url} failed:`, e);
          continue; // Try next proxy
        }
      }

      // Method 3: Try iframe approach (limited by CORS but might work for some)
      if (shortUrl.includes('share.google')) {
        const coords = await tryIframeMethod(shortUrl);
        if (coords) return coords;
      }

      return null;
    } catch (error) {
      console.error('Error resolving short URL:', error);
      return null;
    }
  };

  // Helper to try direct Google Maps access
  // const tryDirectGoogleMapsAccess = async (url: string): Promise<string | null> => {
  //   try {
  //     // This might work for some URLs, though it's limited by CORS
  //     const response = await fetch(url, { 
  //       method: 'HEAD',
  //       mode: 'no-cors' // This allows the request but limits what we can read
  //     });
  //     // We can't read the response due to CORS, but the URL might contain coordinates
  //     return extractCoordsFromContent(url);
  //   } catch (e) {
  //     return null;
  //   }
  // };

  // Try to extract coordinates directly from share URL patterns
  const tryExtractFromShareUrl = (shareUrl: string): string | null => {
    // Some share.google URLs contain encoded data
    // Example: https://share.google/ABC123 might have coordinates in the path
    try {
      const url = new URL(shareUrl);
      const path = url.pathname;
      
      // Try to decode potential coordinate data from the path
      // This is speculative but might work for some URLs
      const pathSegment = path.replace('/', '');
      
      // Check if it looks like a base64 encoded string
      if (pathSegment && pathSegment.length > 10) {
        try {
          // Try different decoding approaches
          const decodedBytes = atob(pathSegment.replace(/-/g, '+').replace(/_/g, '/'));
          const coords = extractCoordsFromContent(decodedBytes);
          if (coords) return coords;
        } catch (e) {
          // Not valid base64, ignore
        }
      }
    } catch (e) {
      // Invalid URL
    }
    
    return null;
  };

  // Try iframe method as fallback
  const tryIframeMethod = (shortUrl: string): Promise<string | null> => {
    return new Promise((resolve) => {
      try {
        // Create a hidden iframe
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.style.width = '1px';
        iframe.style.height = '1px';
        
        let resolved = false;
        const cleanup = () => {
          if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
        };
        
        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            cleanup();
            resolve(null);
          }
        }, 3000);
        
        iframe.onload = () => {
          if (!resolved) {
            try {
              // Try to access iframe content (will fail due to CORS but might reveal URL)
              const iframeUrl = iframe.contentWindow?.location.href;
              if (iframeUrl && iframeUrl !== shortUrl) {
                const coords = extractCoordsFromContent(iframeUrl);
                if (coords) {
                  resolved = true;
                  clearTimeout(timeout);
                  cleanup();
                  resolve(coords);
                  return;
                }
              }
            } catch (e) {
              // Expected CORS error
            }
            
            if (!resolved) {
              resolved = true;
              clearTimeout(timeout);
              cleanup();
              resolve(null);
            }
          }
        };
        
        iframe.onerror = () => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            cleanup();
            resolve(null);
          }
        };
        
        document.body.appendChild(iframe);
        iframe.src = shortUrl;
        
      } catch (e) {
        resolve(null);
      }
    });
  };

  // Enhanced helper function to extract coordinates from HTML content
  const extractCoordsFromContent = (content: string): string | null => {
    // Look for various coordinate patterns in the HTML
    const coordPatterns = [
      // Standard patterns
      /@(-?\d+\.?\d*),(-?\d+\.?\d*)/g,
      /q=(-?\d+\.?\d*),(-?\d+\.?\d*)/g,
      /"lat":(-?\d+\.?\d*).*?"lng":(-?\d+\.?\d*)/g,
      /"latitude":(-?\d+\.?\d*).*?"longitude":(-?\d+\.?\d*)/g,
      /data-lat="(-?\d+\.?\d*)".*?data-lng="(-?\d+\.?\d*)"/g,
      /center=(-?\d+\.?\d*),(-?\d+\.?\d*)/g,
      // Additional patterns for Google Maps
      /ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/g,
      /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/g,
      /!2d(-?\d+\.?\d*)!3d(-?\d+\.?\d*)/g,
      /location=(-?\d+\.?\d*),(-?\d+\.?\d*)/g,
      // URL encoded patterns
      /%2C(-?\d+\.?\d*)%2C(-?\d+\.?\d*)/g,
    ];
    
    for (const pattern of coordPatterns) {
      const matches = [...content.matchAll(pattern)];
      for (const match of matches) {
        if (match && match.length >= 3) {
          const lat = parseFloat(match[1]);
          const lng = parseFloat(match[2]);
          // Additional validation for reasonable coordinates (not zero or obviously wrong)
          if (!isNaN(lat) && !isNaN(lng) && 
              lat >= -90 && lat <= 90 && 
              lng >= -180 && lng <= 180 &&
              (Math.abs(lat) > 0.001 || Math.abs(lng) > 0.001)) {
            return `${lat},${lng}`;
          }
        }
      }
    }
    return null;
  };

  // Function to detect if input is coordinates (lat,lng format) or Google Maps URL
  const isCoordinateInput = (input: string): { lat: number; lng: number } | null => {
    // Remove any whitespace
    const cleanInput = input.trim();
    
    // Check for Google Maps short URLs first
    const shortUrlPatterns = [
      /^(?:https?:\/\/)?(?:www\.)?share\.google\/[a-zA-Z0-9]+$/,
      /^(?:https?:\/\/)?(?:www\.)?goo\.gl\/maps\/[a-zA-Z0-9]+$/,
      /^(?:https?:\/\/)?(?:maps\.)?app\.goo\.gl\/[a-zA-Z0-9]+$/,
    ];
    
    for (const pattern of shortUrlPatterns) {
      if (pattern.test(cleanInput)) {
        // Clear any previous errors
        setLocationError(null);
        setIsResolvingUrl(true);
        
        // Handle short URL asynchronously
        resolveShortUrl(cleanInput).then(coords => {
          setIsResolvingUrl(false);
          if (coords) {
            const [lat, lng] = coords.split(',').map(parseFloat);
            if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
              setSelectedLatLng({ lat, lng });
              setMapCenter({ lat, lng });
              geocodeLatLng({ lat, lng });
              // Clear any previous errors on success
              setLocationError(null);
            }
          } else {
            // More specific error messages
            setLocationError(
              `Unable to resolve the shared link: ${cleanInput}. 
              This might be due to network restrictions or the link format. 
              Try copying coordinates directly from Google Maps instead.`
            );
          }
        }).catch(error => {
          setIsResolvingUrl(false);
          console.error('Error resolving URL:', error);
          setLocationError(
            `Failed to process the shared link. Please try one of these alternatives:
            1. Open the link in Google Maps and copy coordinates
            2. Use a regular Google Maps URL
            3. Enter coordinates directly (e.g., 6.892453, 79.858212)`
          );
        });
        return null; // Return null for now, will be handled asynchronously
      }
    }
    
    // Check for Google Maps URL patterns
    const googleMapsPatterns = [
      // Standard Google Maps URL: https://www.google.com/maps?q=lat,lng
      /(?:https?:\/\/)?(?:www\.)?google\.com\/maps\?q=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      // Google Maps URL with additional parameters: https://maps.google.com/?q=lat,lng
      /(?:https?:\/\/)?(?:maps\.)?google\.com\/\?q=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      // Google Maps URL with maps prefix: https://maps.google.com/maps?q=lat,lng
      /(?:https?:\/\/)?maps\.google\.com\/maps\?q=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      // Google Maps short URL or other variations with q parameter
      /[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      // Google Maps with @lat,lng format: https://www.google.com/maps/@lat,lng,zoom
      /(?:https?:\/\/)?(?:www\.)?google\.com\/maps\/@(-?\d+\.?\d*),(-?\d+\.?\d*)/,
    ];
    
    // Check each pattern
    for (const pattern of googleMapsPatterns) {
      const match = cleanInput.match(pattern);
      if (match) {
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);
        
        // Basic validation for coordinates
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          return { lat, lng };
        }
      }
    }
    
    // If no URL pattern matched, check for direct coordinate input
    // Pattern for coordinates: number,number (with optional spaces and decimal points)
    const coordPattern = /^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/;
    const match = cleanInput.match(coordPattern);
    
    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);
      
      // Basic validation for coordinates
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return { lat, lng };
      }
    }
    
    return null;
  };

  // Handle input change to detect coordinates
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Clear previous error when user starts typing
    if (locationError) {
      setLocationError(null);
    }
    
    // Check if input looks like coordinates
    const coords = isCoordinateInput(value);
    if (coords) {
      // If it's coordinates, handle it directly
      setSelectedLatLng(coords);
      setMapCenter(coords);
      
      // Reverse geocode to get the address
      geocodeLatLng(coords);
    }
  };

  const onPlaceChanged = () => {
    const ac = autocompleteRef.current;
    if (!ac) return;
    const place = ac.getPlace();
    
    // Check if user typed coordinates instead of selecting from autocomplete
    const inputValue = inputRef.current?.value || "";
    const coords = isCoordinateInput(inputValue);
    
    if (coords && !place.geometry) {
      // Handle coordinate input
      setSelectedLatLng(coords);
      setMapCenter(coords);
      geocodeLatLng(coords);
      return;
    }
    
    if (place.geometry && place.geometry.location) {
      const latLng = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      setSelectedLatLng(latLng);
      setMapCenter(latLng);
      setSelectedFormattedAddress(place.formatted_address || inputRef.current?.value || "");
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const filtered = useMemo(() => {
    if (!addresses || addresses.length === 0) return [];
    
    // First filter addresses that have geo locations (latitude and longitude)
    const addressesWithGeoLocation = addresses.filter(
      (addr) => addr.latitude && addr.longitude
    );
    
    // Then apply search filter if there's a search term
    let searchFiltered = addressesWithGeoLocation;
    if (filter.trim()) {
      const f = filter.toLowerCase();
      searchFiltered = addressesWithGeoLocation.filter(
        (a) => a.address.toLowerCase().includes(f) || (a.tag || "").toLowerCase().includes(f)
      );
    }
    
    // Return only the 3 most recently used addresses
    // Assuming the addresses array is ordered from oldest -> newest, take last 3 and reverse to show newest first
    return searchFiltered.slice(-3).reverse();
  }, [addresses, filter]);
  
  // Always compute the last 3 saved addresses with geo (independent of filter/search)
  // const savedLastThree = useMemo(() => {
  //   if (!addresses || addresses.length === 0) return [];
  //   const withGeo = addresses.filter(a => a.latitude && a.longitude);
  //   return withGeo.slice(-3).reverse(); // most recent first
  // }, [addresses]);

  // const handleAdd = () => {
  //   if (!newAddress.trim()) return;
  //   onSelect({ tag: newTag || undefined, address: newAddress.trim() });
  //   setNewTag("");
  //   setNewAddress("");
  // };

  // Use current location
  // const useCurrentLocation = () => {
  //   if (!currentLocation || !selectedFormattedAddress) return;

  //   onSelect({
  //     tag: "Current Location",
  //     address: selectedFormattedAddress,
  //     latitude: currentLocation.lat,
  //     longitude: currentLocation.lng
  //   });
  // };

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton style={{ backgroundColor: '#3f0213', color: 'white' }}>
        <Modal.Title>
          <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
          Choose Delivery Address
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          {(isLoadingAddresses || (addresses && addresses.length > 0)) && (
            <Col md={4} className="mb-3">
              <div className="mb-2">
                <Form.Label>Search saved addresses</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by tag or address"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  disabled={isLoadingAddresses}
                />
              </div>

              <div className="mb-3">
                <Form.Label className="fw-bold">
                  Saved Addresses
                </Form.Label>
                <ListGroup style={{ maxHeight: 400, overflowY: "auto" }}>
                  {isLoadingAddresses ? (
                    <ListGroup.Item className="text-center py-4">
                      <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                      <span className="text-muted">Loading saved addresses...</span>
                    </ListGroup.Item>
                  ) : filteredAddresses.length === 0 ? (
                    <ListGroup.Item className="text-muted text-center py-3">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                      {addresses && addresses.length > 0 
                        ? "No addresses match your search" 
                        : "No saved addresses found"}
                      <br />
                      <small className="text-muted mt-1">
                        Use the map to set a location
                      </small>
                    </ListGroup.Item>
                  ) : (
                    filteredAddresses.map((addr, idx) => (
                      <ListGroup.Item 
                        key={addr.id || idx} 
                        className="d-flex justify-content-between align-items-start address-item"
                      >
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <div className="d-flex align-items-center">
                              {addr.tag && (
                                <>
                                  <FontAwesomeIcon
                                    icon={faMapMarkerAlt}
                                    className="me-2 text-primary"
                                  />
                                  <strong className="text-primary">{addr.tag}</strong>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="text-muted small mb-1">{addr.address}</div>
                          {addr.latitude && addr.longitude && (
                            <small className="text-success d-block">
                              📍 {addr.latitude.toFixed(4)}, {addr.longitude.toFixed(4)}
                            </small>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => onSelect(addr)}
                          className="ms-2 flex-shrink-0"
                        >
                          <FontAwesomeIcon icon={faCheck} className="me-1" />
                          Use
                        </Button>
                      </ListGroup.Item>
                    ))
                  )}
                </ListGroup>
                {!isLoadingAddresses && addresses && addresses.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                      Showing {filteredAddresses.length} of {addresses.length} saved addresses
                    </small>
                  </div>
                )}
              </div>
            </Col>
          )}

          <Col md={(isLoadingAddresses || (addresses && addresses.length > 0)) ? 8 : 12}>
            <div className="mb-2">
              <Form.Label className="fw-bold">Search Location</Form.Label>
              {isLoaded ? (
                <>
                  <Autocomplete
                    onLoad={(ac) => {
                      onAutocompleteLoad(ac);
                      try {
                        ac.setComponentRestrictions?.({ country: ["lk"] });
                      } catch { }
                    }}
                    onPlaceChanged={onPlaceChanged}
                    options={{
                      componentRestrictions: { country: ["lk"] },
                      fields: ["formatted_address", "geometry", "name", "address_components", "place_id", "types", "business_status"],
                    }}
                  >
                    <Form.Control
                      ref={inputRef}
                      type="text"
                      placeholder="Search place, enter coordinates, or paste Google Maps URL"
                      onChange={handleSearchInputChange}
                      disabled={isResolvingUrl}
                    />
                  </Autocomplete>
                  <small className="text-muted">
                    💡 You can search places, enter coordinates (6.892453,79.858212), or paste any Google Maps URLs (including share links)
                    {isResolvingUrl && (
                      <div className="mt-1">
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        <span className="text-primary">Resolving shared link...</span>
                      </div>
                    )}
                    {locationError && (
                      <div className="mt-2 p-2 border border-warning rounded bg-light">
                        <div className="text-warning small">
                          <strong>⚠️ Link Resolution Issue:</strong>
                        </div>
                        <div className="text-muted small mt-1" style={{ whiteSpace: 'pre-line' }}>
                          {locationError}
                        </div>
                        <div className="mt-2">
                          <small className="text-info">
                            <strong>📱 Quick tip for share.google links:</strong><br/>
                            1. Open the link in your browser<br/>
                            2. Wait for it to redirect to Google Maps<br/>
                            3. Copy the URL from the address bar or right-click the location pin and select "What's here?"<br/>
                            4. Use those coordinates here instead
                          </small>
                        </div>
                      </div>
                    )}
                  </small>
                </>
              ) : (
                <Form.Control disabled placeholder="Loading map..." />
              )}
            </div>

            <div style={{ width: "100%", height: 450 }}>
              {isLoaded && (
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                  center={mapCenter}
                  zoom={currentLocation ? 16 : 14}
                  onClick={handleMapClick}
                  options={{
                    disableDefaultUI: false,
                    clickableIcons: false,
                    zoomControl: true,
                    streetViewControl: false,
                    fullscreenControl: false
                  }}
                >
                  {currentLocation && (
                    <Marker
                      position={currentLocation}
                      icon={{
                        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="8" fill="#4285F4" stroke="white" stroke-width="2"/>
                            <circle cx="12" cy="12" r="3" fill="white"/>
                          </svg>
                        `),
                        scaledSize: new window.google.maps.Size(24, 24),
                        anchor: new window.google.maps.Point(12, 12)
                      }}
                      title="Your current location"
                    />
                  )}
                  {selectedLatLng && (!currentLocation ||
                    (selectedLatLng.lat !== currentLocation.lat || selectedLatLng.lng !== currentLocation.lng)) && (
                      <Marker position={selectedLatLng} />
                    )}
                </GoogleMap>
              )}
            </div>

            <div className="mt-2 d-flex justify-content-between align-items-center">
              <div className="text-muted small" style={{ maxWidth: "70%" }}>
                {selectedFormattedAddress || "Click on map or search to choose a location"}
              </div>
              <Button
                variant="success"
                size="sm"
                disabled={!selectedLatLng}
                onClick={() => {
                  if (!selectedLatLng) return;
                  const isCurrentLoc = currentLocation &&
                    selectedLatLng.lat === currentLocation.lat &&
                    selectedLatLng.lng === currentLocation.lng;

                  onSelect({
                    tag: isCurrentLoc ? "Home" : "Delivery",
                    address: selectedFormattedAddress || `${selectedLatLng.lat}, ${selectedLatLng.lng}`,
                    latitude: selectedLatLng.lat,
                    longitude: selectedLatLng.lng
                  });
                }}
              >
                <FontAwesomeIcon icon={faCheck} className="me-1" />
                Use this location
              </Button>
            </div>
          </Col>
        </Row>

        {/* <hr />

        <Form.Label className="mb-2">Add new address</Form.Label>
        <InputGroup className="mb-2">
          <InputGroup.Text style={{ minWidth: 60 }}>Tag</InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="e.g., Home, Office"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
          />
        </InputGroup>
        <InputGroup>
          <InputGroup.Text>Address</InputGroup.Text>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="Enter new delivery address"
            value={newAddress}
            onChange={(e) => setNewAddress(e.target.value)}
          />
        </InputGroup>
        <div className="d-flex justify-content-end mt-2">
          <Button variant="success" onClick={handleAdd} disabled={!newAddress.trim()}>
            Use this address
          </Button>
        </div> */}

      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeliveryAddressModal;


